import { createTheme, alpha, type Theme } from '@mui/material/styles';

/**
 * PMC design system.
 *
 * The palette is carried over from the legacy portal's stylesheet so the
 * rebuilt portal is recognisably the same service — navy for the utility strip
 * and headings, indigo for interaction. Everything else (spacing, elevation,
 * typography scale) is rebuilt on MUI's system rather than the original's
 * hand-rolled CSS.
 *
 * Devanagari matters here: Inter has no Devanagari glyphs, so Noto Sans
 * Devanagari must follow it in the stack or every Marathi string falls back to
 * a system font and the page loses its typographic identity mid-sentence.
 */

export const PMC = {
  navy: '#0e1b45',
  navyLight: '#1b2c63',
  primary: '#443eae',
  primaryDark: '#37327f',
  primaryLight: '#eef0fa',
  ink: '#1f2937',
  muted: '#64748b',
  line: '#e5e7eb',
  bgSoft: '#f6f7fb',
  success: '#15803d',
  successBg: '#e7f7ed',
  warning: '#b45309',
  warningBg: '#fdf3d7',
  danger: '#b91c1c',
  dangerBg: '#fdeaea',
} as const;

/** Categorical series for the dashboard charts — distinguishable, print-safe. */
export const CHART_COLORS = [
  '#443eae',
  '#0891b2',
  '#7c3aed',
  '#0d9488',
  '#c2410c',
  '#4f46e5',
  '#be123c',
  '#65a30d',
] as const;

const FONT_STACK = [
  'Inter',
  '"Noto Sans Devanagari"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'sans-serif',
].join(', ');

export const theme: Theme = createTheme({
  palette: {
    primary: { main: PMC.primary, dark: PMC.primaryDark, light: PMC.primaryLight },
    secondary: { main: PMC.navy, light: PMC.navyLight },
    success: { main: PMC.success },
    warning: { main: PMC.warning },
    error: { main: PMC.danger },
    text: { primary: PMC.ink, secondary: PMC.muted },
    divider: PMC.line,
    background: { default: PMC.bgSoft, paper: '#ffffff' },
  },

  shape: { borderRadius: 10 },

  typography: {
    fontFamily: FONT_STACK,
    // The legacy portal capped weights at 700 to stay institutional rather
    // than promotional; that restraint is kept.
    h1: { fontSize: '2.4rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15 },
    h2: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.015em' },
    h3: { fontSize: '1.35rem', fontWeight: 700, letterSpacing: '-0.01em' },
    h4: { fontSize: '1.15rem', fontWeight: 700 },
    h5: { fontSize: '1rem', fontWeight: 600 },
    h6: { fontSize: '0.9rem', fontWeight: 600 },
    subtitle2: { fontSize: '0.8rem', fontWeight: 600, color: PMC.muted },
    body1: { fontSize: '0.925rem' },
    body2: { fontSize: '0.85rem' },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    caption: { fontSize: '0.75rem' },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // The legacy stylesheet runs a 13.5px root and every rem-based size
        // in this theme is written assuming that scale. Rem units resolve
        // against the browser's actual root font-size (16px by default), not
        // any MUI theme constant, so this has to be a real CSS rule — without
        // it the whole app renders ~18% larger/heavier than the original.
        html: { fontSize: '13.5px' },
        // Numeric columns must line up: tabular figures stop amounts from
        // jittering as digits change width.
        '.num': { fontVariantNumeric: 'tabular-nums' },
        '@media print': {
          '.no-print': { display: 'none !important' },
          body: { background: '#fff' },
          // Browsers drop background colours on print by default. Without
          // this, every navy banner and tinted summary row on the bill
          // document (and anything similar elsewhere) prints as plain white
          // text on white — the legacy stylesheet sets this same rule for
          // the same reason.
          '*': {
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
          },
        },
        // Printed page is narrower than the on-screen container once the
        // browser's own default margins (often 0.5-1in a side) come off —
        // the bill document's dense tables (nowrap PID/bill-no cells) need
        // that width back, not the default margin.
        '@page': { margin: '0.35in' },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: PMC.line },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${PMC.line}`,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 8, paddingInline: 18 },
        containedPrimary: {
          '&:hover': { backgroundColor: PMC.primaryDark },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: '0.72rem', height: 24 },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: PMC.bgSoft,
          fontWeight: 700,
          fontSize: '0.78rem',
          color: PMC.ink,
          whiteSpace: 'nowrap',
        },
        root: { fontSize: '0.85rem', borderColor: PMC.line },
      },
    },

    MuiTextField: { defaultProps: { size: 'small' } },
    MuiSelect: { defaultProps: { size: 'small' } },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, alignItems: 'center' },
        standardInfo: { backgroundColor: PMC.primaryLight },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: PMC.navy, fontSize: '0.75rem' },
      },
    },

    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { fontWeight: 500 } },
    },
  },
});

/** Soft tinted surface for stat tiles and callouts. */
export function tint(color: string, amount = 0.08): string {
  return alpha(color, amount);
}
